import React, { useState, useEffect, useMemo } from 'react';

import resourceOneService from '../../../service/resources/resourceOne.service';
import { dataGridPremiumColumns, generateColumnsFromDynProps } from '../../../utils/helper';
import { DataGridIdentifiers, OrderArrowsUI, DatagridPremiumUi } from '../../Interface';
import { useLocation } from 'react-router-dom';
import useGenerateJokerReportCols from '../../../hooks/useGenerateJokerReportCols';

export default function ResourceOneReports({ resourceOneState }) {
  const {
    refetchTable,
    setRefetchTable,
    setSelectedValues,
    setRowSelectionModel,
    rowSelectionModel,
    viewField,
    operatorsDisabled,
    dynProps,
    autocompleteOptions,
    selectedValues,
    roles,
    params,
    jokerFields,
  } = resourceOneState;
  const location = useLocation();
  const [reports, setReports] = useState({ rows: [], rowCount: 0 });

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'name', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  useEffect(() => {
    if (operatorsDisabled) {
      setRowSelectionModel([]);
    }
  }, [operatorsDisabled]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0 && reports?.rows.length > 0) {
      if (location?.state?.from === 'widget3') {
        setRowSelectionModel([reports?.rows[0]?.id]);
        setSelectedValues(reports?.rows[0] ?? {});
        if (rowSelectionModel.length > 0) {
          location.state = null;
        }
      }
    }
  }, [selectedValues]);

  const getList = (gridState) => {
    const parsedParam = params.VIEW_RESOURCEONE_BY_CLASSIFICATION_SEE_ALL
      ? parseInt(params.VIEW_RESOURCEONE_BY_CLASSIFICATION_SEE_ALL)
      : null;

    const roleClassificationId = roles?.VIEW_RESOURCEONE_BY_CLASSIFICATION
      ? parseInt(roles.VIEW_RESOURCEONE_BY_CLASSIFICATION)
      : null;

    if (parsedParam !== null && roleClassificationId === null) {
      setReports({ rows: [], rowCount: 0 });
      return;
    }

    const shouldAddFilter = parsedParam !== null && parsedParam !== roleClassificationId;

    const additionalFilters = shouldAddFilter
      ? [{ field: 'classificationId', value: roleClassificationId, operator: 'is' }]
      : [];

    const stateWithUserFilter = {
      ...gridState,
      filter: {
        ...gridState.filter,
        filterModel: {
          ...gridState.filter?.filterModel,
          items: [...(gridState.filter?.filterModel?.items || []), ...additionalFilters],
          logicOperator: 'and',
        },
      },
    };

    resourceOneService.getResources(stateWithUserFilter).then((data) => {
      setReports(data);
    });
  };

  const orderResource = (id, newOrder) => {
    resourceOneService.orderResource(id, { newOrder }).then(() => {
      setRefetchTable(true);
    });
  };

  const handleOrderChange = (id, direction) => {
    orderResource(id, direction);
  };

  const jokerCols = useGenerateJokerReportCols(jokerFields);

  const columns = useMemo(() => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        type: 'string',
        headerName: 'Név',
        isRequired: true,
      },
      {
        field: 'groupId',
        type: 'singleSelect',
        headerName: 'Erőforrás csoportja',
        renderCell: (data) => {
          return data.row.resourceGroup?.itemName;
        },
        valueOptions: autocompleteOptions.resourceGroup,
      },
      {
        field: 'statusId',
        type: 'singleSelect',
        headerName: 'Státusz',
        renderCell: (data) => {
          return data.row.status?.itemName;
        },
        valueOptions: autocompleteOptions.status,
      },
      {
        field: 'active',
        type: 'boolean',
        headerName: 'Aktív',
        sortable: false,
      },
      {
        field: 'typeId',
        type: 'singleSelect',
        headerName: 'Típus',
        renderCell: (data) => {
          return data.type?.itemName;
        },
        sortable: false,
        valueOptions: autocompleteOptions.type,
      },
      {
        field: 'classificationId',
        type: 'singleSelect',
        headerName: 'Besorolás',
        renderCell: (data) => {
          return data.classification?.name;
        },
        sortable: false,
        valueOptions: autocompleteOptions.classification,
      },
      {
        field: 'visOrder',
        headerName: 'Sorrend',
        type: 'action',
        filterable: false,
        renderCell: (params) => {
          const disabled = params?.api?.getSortModel()[0]?.field !== 'visOrder';
          const isSortedDescending = params.api.getSortModel()[0]?.sort === 'desc';

          const handleUpClick = () => {
            if (isSortedDescending) {
              handleOrderChange(params.row.id, 'down');
            } else {
              handleOrderChange(params.row.id, 'up');
            }
          };

          const handleDownClick = () => {
            if (isSortedDescending) {
              handleOrderChange(params.row.id, 'up');
            } else {
              handleOrderChange(params.row.id, 'down');
            }
          };

          return <OrderArrowsUI onUpClick={handleUpClick} onDownClick={handleDownClick} disabled={disabled} />;
        },
      },
      {
        field: 'color',
        headerName: 'Szín',
        type: 'string',
        sortable: false,
        filterable: false,
        renderCell: (data) => {
          return data.row.color ? (
            <div
              className="rounded-full shadow-[rgba(0,_0,_0,_0.54)_0px_1px_8px] w-[20px] h-[20px] hover:cursor-pointer mt-1"
              style={{ background: data.row.color }}
            />
          ) : (
            ''
          );
        },
      },
      {
        field: 'phoneNumber',
        type: 'string',
        headerName: 'Telefonszám',
      },
      {
        field: 'email',
        type: 'string',
        headerName: 'Email',
      },
      {
        field: 'position',
        type: 'string',
        headerName: 'Pozíció',
      },
      {
        field: 'specialization',
        type: 'string',
        headerName: 'Szakterület',
      },
      ...(params.HIDE_RES1_RESOURCEONE_GROUP_FIELD !== 'Y'
        ? [
            {
              field: 'resourceOneGroupId',
              type: 'singleSelect',
              headerName: 'Csoport',
              renderCell: (data) => {
                return data.row.resourceOneGroup?.name;
              },
              valueOptions: autocompleteOptions.resourceOneGroup,
            },
          ]
        : []),
      ...generateColumnsFromDynProps(dynProps ?? []),
      ...jokerCols,
    ]);
  }, [autocompleteOptions, dynProps, jokerCols]);

  return (
    <DatagridPremiumUi
      datagridIdentifier={DataGridIdentifiers.resourceOne}
      datagridInitialState={initialState}
      dataLoader={getList}
      rows={reports.rows}
      refetch={{ refetchTable, setRefetchTable }}
      rowCount={reports.rowCount}
      columns={[...columns]}
      onRowClick={(rowData) => {
        if (!viewField) return;
        setSelectedValues(rowData.row);
      }}
      paginationAndfilterMode="server"
      onRowSelectionModelChange={(newRowSelectionModel) => {
        setRowSelectionModel(newRowSelectionModel);
      }}
      rowSelectionModel={rowSelectionModel}
      disableRowSelectionOnClick={!viewField}
    />
  );
}
